import React, { useEffect, useRef } from 'react';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';

// 1. Swap the standard Image extension for the Resizable package
import { ResizableImage } from 'tiptap-extension-resizable-image';
import 'tiptap-extension-resizable-image/styles.css';

// 2. Extend ResizableImage instead of the default Image
const CustomImage = ResizableImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: element => element.getAttribute('id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {};
          }
          return { id: attributes.id };
        },
      },
    };
  },
});

const InsertImageControl = ({ onImageUpload, editor }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && onImageUpload) {
      try {
        const uploadedImage = await onImageUpload(file);
        
        if (uploadedImage?.url) {
          // 3. Update the Tiptap command to setResizableImage
          editor.chain().focus().setResizableImage({ 
            src: uploadedImage.url, 
            id: uploadedImage.id 
          }).run();
        }
      } catch (error) {
        console.error("Image upload failed", error);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <RichTextEditor.Control
        onClick={() => fileInputRef.current?.click()}
        aria-label="Bild einfügen"
        title="Bild einfügen"
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 1.5C1.94772 1.5 1.5 1.94772 1.5 2.5V12.5C1.5 13.0523 1.94772 13.5 2.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V2.5C13.5 1.94772 13.0523 1.5 12.5 1.5H2.5ZM2.5 2.5H12.5V9.45228L11.8385 8.79075C11.6432 8.59549 11.3268 8.59549 11.1315 8.79075L9.5 10.4222L6.86853 7.79075C6.67327 7.59549 6.35685 7.59549 6.16159 7.79075L2.5 11.4523V2.5ZM2.5 12.5V12.8665L5.80806 9.55845L8.43953 12.1899C8.63479 12.3852 8.95121 12.3852 9.14647 12.1899L10.7779 10.5585L12.5 12.2805V12.5H2.5ZM10.5 4.5C9.94772 4.5 9.5 4.94772 9.5 5.5C9.5 6.05228 9.94772 6.5 10.5 6.5C11.0523 6.5 11.5 6.05228 11.5 5.5C11.5 4.94772 11.0523 4.5 10.5 4.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
        </svg>
      </RichTextEditor.Control>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />
    </>
  );
};

export default function CustomRichTextEditor({ value, onChange, onImageUpload, className, enableImage=false }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      CustomImage, // Your custom extended resizable image handles rendering
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <RichTextEditor editor={editor} className={className}>
      {/* ... [Keep your existing toolbar code exactly as is] ... */}
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.AlignLeft />
          <RichTextEditor.AlignCenter />
          <RichTextEditor.AlignRight />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          {enableImage && <InsertImageControl onImageUpload={onImageUpload} editor={editor} />}
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content />
    </RichTextEditor>
  );
}